import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import IconSvg from 'Components/IconSvg';
import MediaCard from 'Components/profile/MediaCard';
import MediaCardSkeleton from 'Components/profile/MediaCardSkeleton';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
const MIN_SKELETON_ITEMS = 20;

const PrevButton = ({ isVisible, onClick, title }) => {
  return isVisible ? (
    <button
      className="media-rail__button media-rail__button-left"
      onClick={onClick}
      title={title}
    >
      <IconSvg title={title} type="arrowLeft" />
    </button>
  ) : null;
};

const NextButton = ({ isVisible, onClick, title }) => {
  return isVisible ? (
    <button
      className="media-rail__button media-rail__button-right"
      onClick={onClick}
      title={title}
    >
      <IconSvg title={title} type="arrowRight" />
    </button>
  ) : null;
};

const MediaRail = ({
  country,
  hasError,
  isLoading,
  itemList,
  itemMap,
  onItemActionClick,
  onItemClick,
  onLoadMore,
  shouldLoadMore,
  shouldReset,
  strings,
  totalCount,
}) => {
  const [skeletonItems, setSkeletonItems] = useState([
    ...Array(MIN_SKELETON_ITEMS),
  ]);
  const [swipe, setSwipe] = useState(false);
  const [slidesToScroll, setSlidesToScroll] = useState(1);
  const [activeItemIdx, setActiveItemIdx] = useState(0);
  const railRef = useRef(null);
  const sliderRef = useRef(null);
  const currentCount = itemList.length;

  const isNextButtonVisible = () => {
    // Slider default will slide until last item is the active slide
    // Instead hide button if no remaining slide 'frames' exist
    const slideFrameIdx = activeItemIdx / slidesToScroll;
    const scrolledItemCount = (slideFrameIdx + 1) * slidesToScroll;
    return scrolledItemCount < currentCount;
  };

  const sliderSettings = {
    beforeChange: (current, next) => {
      setActiveItemIdx(next);
      if (next > currentCount / 2) handleLoadMore();
    },
    className: 'media-rail__items',
    dots: false,
    easing: 'ease-out',
    infinite: false,
    nextArrow: (
      <NextButton isVisible={isNextButtonVisible()} title={strings.viewMore} />
    ),
    prevArrow: (
      <PrevButton isVisible={activeItemIdx} title={strings.viewMore} />
    ),
    slidesToScroll,
    speed: 300,
    swipe,
    variableWidth: true,
  };

  const calculateDimensions = () => {
    const railWidth = railRef.current.clientWidth;
    const railItem = railRef.current.querySelector('.media-rail__item');
    const { marginLeft, marginRight, width } = getComputedStyle(railItem);
    const railItemWidth =
      parseFloat(width) + parseFloat(marginLeft) + parseFloat(marginRight);
    const maxRailItemCount = Math.floor(railWidth / railItemWidth);
    setSlidesToScroll(maxRailItemCount);
    setSwipe(window.innerWidth < 768);
  };

  const handleLoadMore = () => {
    if (shouldLoadMore) onLoadMore();
  };

  const renderItem = (id) => {
    const item = itemMap[id];
    return (
      <MediaCard
        {...item}
        country={country}
        onActionClick={onItemActionClick}
        onClick={onItemClick}
        strings={strings}
      />
    );
  };

  const renderSliderContents = () => {
    if (isLoading || hasError || !totalCount)
      return skeletonItems.map((item, idx) => (
        <div className="media-rail__item" key={idx}>
          <MediaCardSkeleton />
        </div>
      ));

    return itemList.map((id) => (
      <div className="media-rail__item" key={id}>
        {renderItem(id)}
      </div>
    ));
  };

  useLayoutEffect(() => {
    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => {
      window.removeEventListener('resize', calculateDimensions);
    };
  }, []);

  useEffect(() => {
    // Match skeleton item count to total item count
    // Helps slide transition below appear more fluid when changing content
    if (totalCount > MIN_SKELETON_ITEMS)
      setSkeletonItems([...new Array(totalCount)]);
    // Slide to first card on first load
    if (isLoading && !totalCount) sliderRef?.current?.slickGoTo(0, true);
  }, [isLoading, totalCount]);

  return (
    <div className="media-rail" ref={railRef}>
      <Slider ref={sliderRef} {...sliderSettings}>
        {renderSliderContents()}
      </Slider>
    </div>
  );
};

MediaRail.propTypes = {
  country: PropTypes.string.isRequired,
  hasError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  itemList: PropTypes.array.isRequired,
  itemMap: PropTypes.object.isRequired,
  onItemActionClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func,
  onLoadMore: PropTypes.func,
  shouldLoadMore: PropTypes.bool,
  strings: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
};

MediaRail.defaultProps = {
  onItemClick: () => {},
  onLoadMore: () => {},
  shouldLoadMore: false,
};

export default MediaRail;
